import React from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, Paper,
    Stack, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import {
    IVerbaScholaLessonReviewIssue, IVerbaScholaLessonReviewIssues, lessonAuthoringError, VerbaScholaApi,
} from '../api/VerbaScholaApi';
import { BORDER, ERROR_TEXT, INTER, NAVY, SLATE, WARNING_TEXT } from '../style/verbaScholaStyles';
import LessonText from './LessonText';

export const LESSON_REVIEW_ISSUES_CHANGED = 'verbaschola-lesson-review-issues-changed';

type ReviewFilter = 'open' | 'all' | 'plan' | 'exercise' | 'image';
const storageKey = (lessonId: number, name: string) => `verbaschola-lesson-review:${lessonId}:${name}`;
type PopupPosition = { left: number; top: number };

function readSession(lessonId: number, name: string): string | null {
    try { return window.sessionStorage.getItem(storageKey(lessonId, name)); }
    catch { return null; }
}

function writeSession(lessonId: number, name: string, value: string | null) {
    try {
        if (value === null) window.sessionStorage.removeItem(storageKey(lessonId, name));
        else window.sessionStorage.setItem(storageKey(lessonId, name), value);
    } catch { /* Storage can be unavailable in a restricted browser. */ }
}

function readPosition(lessonId: number): PopupPosition | null {
    try {
        const saved = readSession(lessonId, 'position');
        if (!saved) return null;
        const position: PopupPosition = JSON.parse(saved);
        return Number.isFinite(position.left) && Number.isFinite(position.top) ? position : null;
    } catch { return null; }
}

function readFilter(lessonId: number, pageScope?: 'plan' | 'exercises'): ReviewFilter {
    const stored = readSession(lessonId, pageScope ? `filter:${pageScope}` : 'filter');
    const allowed: ReviewFilter[] = pageScope === 'plan' ? ['open', 'plan', 'all']
        : pageScope === 'exercises' ? ['open', 'exercise', 'image', 'all']
            : ['open', 'plan', 'exercise', 'image', 'all'];
    return allowed.includes(stored as ReviewFilter) ? stored as ReviewFilter : 'open';
}

function withinViewport(position: PopupPosition, width: number, height: number, reservedRight = 0): PopupPosition {
    const inset = 8;
    return {
        left: Math.min(Math.max(position.left, inset), Math.max(inset, window.innerWidth - width - inset - reservedRight)),
        top: Math.min(Math.max(position.top, inset), Math.max(inset, window.innerHeight - height - inset)),
    };
}

/** Keep the floating review visible when the teacher switches between plan and exercise tabs. */
export function useLessonReviewOpen(lessonId: number, routeIssueId: string | null) {
    const [open, setOpenState] = React.useState(() => Boolean(routeIssueId) || readSession(lessonId, 'open') === 'true');
    const setOpen = React.useCallback((next: boolean) => {
        setOpenState(next);
        writeSession(lessonId, 'open', next ? 'true' : null);
    }, [lessonId]);
    React.useEffect(() => {
        if (routeIssueId) setOpen(true);
        else setOpenState(readSession(lessonId, 'open') === 'true');
    }, [lessonId, routeIssueId, setOpen]);
    return [open, setOpen] as const;
}

const needsReview = (issue: IVerbaScholaLessonReviewIssue) => issue.status === 'open' || issue.status === 'changed';

/** Both editor pages can show a live count without depending on the copilot. */
export function useLessonReviewIssues(lessonId: number, refreshKey?: string | number) {
    const [result, setResult] = React.useState<IVerbaScholaLessonReviewIssues>({
        issues: [], counts: { open: 0, warning: 0, error: 0 },
    });
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [revision, setRevision] = React.useState(0);
    const refresh = React.useCallback(() => setRevision(current => current + 1), []);

    React.useEffect(() => {
        setResult({ issues: [], counts: { open: 0, warning: 0, error: 0 } });
    }, [lessonId]);

    React.useEffect(() => {
        const onChanged = (event: Event) => {
            if ((event as CustomEvent<{ lessonId: number }>).detail?.lessonId === lessonId) refresh();
        };
        window.addEventListener(LESSON_REVIEW_ISSUES_CHANGED, onChanged);
        return () => window.removeEventListener(LESSON_REVIEW_ISSUES_CHANGED, onChanged);
    }, [lessonId, refresh]);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        void VerbaScholaApi.getLessonReviewIssues(lessonId).then(response => {
            if (!cancelled) setResult(response.data);
        }).catch(() => {
            if (!cancelled) setError('Could not load lesson review items.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [lessonId, refreshKey, revision]);

    return { ...result, loading, error, refresh };
}

function sourceLabel(source: IVerbaScholaLessonReviewIssue['source']) {
    if (source === 'image') return 'Picture';
    if (source === 'plan') return 'Lesson plan';
    return 'Exercise';
}

function categoryLabel(category: string) {
    const labels: Record<string, string> = {
        chinese: 'Chinese wording', quiz: 'Quiz answer', pinyin: 'Pinyin', coverage: 'Target coverage',
        plan_timing: 'Plan timing', textbook_image: 'Textbook picture', plan_notice: 'Plan note',
        lesson_notice: 'Lesson details', target_notice: 'Language targets', material_notice: 'Materials',
        exercise_notice: 'Exercise note', image_quality: 'AI picture quality',
        picture_confirmation: 'Picture checklist', image_broken: 'Broken picture',
        image_description_missing: 'Missing picture description', plan_empty: 'Empty plan',
        exercise_empty: 'No exercises', ready_activity_invalid: 'Invalid Ready activity',
        lesson_content_too_large: 'Ready content size', pending_proposal: 'Unapplied proposal',
        generation_failed: 'Generation failed',
    };
    if (labels[category]) return labels[category];
    return category.replaceAll('_', ' ').replace(/^\w/u, initial => initial.toUpperCase());
}

export function reviewIssueSessionNumber(issue: IVerbaScholaLessonReviewIssue): number | null {
    if (issue.source !== 'exercise' || issue.exercise_id || issue.step_id) return null;
    const mentions = [...`${issue.title} ${issue.detail}`.matchAll(/\bsession\s+#?(\d{1,3})\b/giu)]
        .map(match => Number(match[1])).filter(number => number >= 1 && number <= 200);
    const unique = [...new Set(mentions)];
    return unique.length === 1 ? unique[0] : null;
}

function issueLocation(issue: IVerbaScholaLessonReviewIssue) {
    if (issue.action_required === 'generation_job') return issue.category === 'pending_proposal' ? 'Review proposal' : 'Open generation history';
    if (issue.source === 'plan') {
        if (issue.generated_material_id) return 'Open material';
        if (issue.vocabulary_target_id || issue.grammar_target_id) return 'Open language target';
        return issue.plan_section_id ? 'Open plan item' : 'Open plan overview';
    }
    if (!issue.exercise_id && !issue.step_id) {
        const sessionNumber = reviewIssueSessionNumber(issue);
        return sessionNumber ? `Open session ${sessionNumber}` : 'Open exercise overview';
    }
    if (issue.source === 'image') return 'Open picture';
    return 'Open exercise';
}

function filterIssues(issues: IVerbaScholaLessonReviewIssue[], filter: ReviewFilter) {
    if (filter === 'open') return issues.filter(needsReview);
    if (filter === 'all') return issues;
    return issues.filter(issue => issue.source === filter && needsReview(issue));
}

function issueOrder(a: IVerbaScholaLessonReviewIssue, b: IVerbaScholaLessonReviewIssue) {
    const priority = (issue: IVerbaScholaLessonReviewIssue) => {
        if (!needsReview(issue)) return 3;
        if (issue.severity === 'error') return 0;
        if (issue.source === 'image' && issue.category === 'image_quality') return 1;
        return 2;
    };
    return priority(a) - priority(b);
}

export interface VerbaScholaLessonReviewNavigatorProps {
    lessonId: number;
    /** Shared page-level result, so opening the popup does not request the queue twice. */
    review: ReturnType<typeof useLessonReviewIssues>;
    open: boolean;
    onClose: () => void;
    /** Return a message when the editor cannot open the selected item. */
    onOpen: (issue: IVerbaScholaLessonReviewIssue) => string | null | void;
    /** Used when navigating between the plan and exercise routes. */
    initialIssueId?: string | number | null;
    /** A page banner can select and open a specific finding, including on repeat clicks. */
    focusRequest?: { issueId: number; key: number } | null;
    /** Refresh page-level review badges after a teacher decision. */
    onChanged?: () => void;
    /** The compact copilot uses a right-side drawer below the desktop breakpoint. */
    copilotOpen?: boolean;
    /** A request sent to the copilot (Revise with AI): the review, which sits over the copilot, folds to its bar. */
    copilotRequest?: number | null;
    /** The parent supplies findings for this page only. This also scopes the available filters. */
    pageScope?: 'plan' | 'exercises';
}

/** A compact review surface scoped by its parent page and independent of the generation copilot. */
export default function VerbaScholaLessonReviewNavigator({
    lessonId, review, open, onClose, onOpen, initialIssueId, focusRequest, onChanged, copilotOpen = false, copilotRequest, pageScope,
}: VerbaScholaLessonReviewNavigatorProps) {
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const compactCopilot = useMediaQuery(theme.breakpoints.down('lg'));
    const drawerOpen = compactCopilot && copilotOpen;
    const drawerSpace = React.useCallback(() => drawerOpen ? Math.min(380, window.innerWidth * 0.92) : 0, [drawerOpen]);
    const paperRef = React.useRef<HTMLDivElement | null>(null);
    const dragRef = React.useRef<{ pointerId: number; x: number; y: number; left: number; top: number; width: number; height: number } | null>(null);
    const [position, setPosition] = React.useState<PopupPosition | null>(() => readPosition(lessonId));
    const positionRef = React.useRef(position);
    const { issues, counts, loading, error, refresh } = review;
    const activeStorageName = pageScope ? `active:${pageScope}` : 'active';
    const filterStorageName = pageScope ? `filter:${pageScope}` : 'filter';
    const [filter, setFilter] = React.useState<ReviewFilter>(() => readFilter(lessonId, pageScope));
    const [showList, setShowList] = React.useState(false);
    const [collapsed, setCollapsed] = React.useState(() => readSession(lessonId, 'collapsed') === 'true');
    const routeIssueId = initialIssueId === undefined || initialIssueId === null || initialIssueId === ''
        ? null : Number(initialIssueId);
    const validRouteIssueId = routeIssueId !== null && Number.isSafeInteger(routeIssueId) && routeIssueId !== 0 ? routeIssueId : null;
    const storedIssueId = Number(readSession(lessonId, activeStorageName));
    const [activeId, setActiveId] = React.useState<number | null>(validRouteIssueId
        ?? (Number.isSafeInteger(storedIssueId) && storedIssueId !== 0 ? storedIssueId : null));
    const [decisionPending, setDecisionPending] = React.useState(false);
    const [decisionError, setDecisionError] = React.useState<string | null>(null);
    const [navigationError, setNavigationError] = React.useState<string | null>(null);
    const handledFocusRequest = React.useRef<{ lessonId: number; key: number } | null>(null);

    const rememberFilter = React.useCallback((next: ReviewFilter) => {
        setFilter(next);
        setNavigationError(null);
        writeSession(lessonId, filterStorageName, next);
    }, [lessonId, filterStorageName]);

    const rememberActiveId = React.useCallback((next: number | null) => {
        setActiveId(next);
        writeSession(lessonId, activeStorageName, next === null ? null : String(next));
    }, [lessonId, activeStorageName]);

    const movePopup = React.useCallback((next: PopupPosition) => {
        positionRef.current = next;
        setPosition(next);
        writeSession(lessonId, 'position', JSON.stringify(next));
    }, [lessonId]);

    React.useEffect(() => {
        const saved = readPosition(lessonId);
        positionRef.current = saved;
        setPosition(saved);
    }, [lessonId]);

    React.useEffect(() => {
        if (!open || !paperRef.current) return;
        const paper = paperRef.current;
        const keepVisible = () => {
            const current = positionRef.current;
            if (!current) return;
            const rect = paper.getBoundingClientRect();
            const next = withinViewport(current, rect.width, rect.height, drawerSpace());
            if (next.left !== current.left || next.top !== current.top) movePopup(next);
        };
        keepVisible();
        window.addEventListener('resize', keepVisible);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keepVisible);
        observer?.observe(paper);
        return () => {
            window.removeEventListener('resize', keepVisible);
            observer?.disconnect();
        };
    }, [open, lessonId, movePopup, drawerSpace]);

    const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0 || !event.isPrimary || !paperRef.current) return;
        // Title-bar controls must keep their normal click and touch behavior.
        if (event.target instanceof Element && event.target.closest(
            'button, a, input, select, textarea, [contenteditable="true"], [role="button"]',
        )) return;
        const rect = paperRef.current.getBoundingClientRect();
        dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY,
            left: rect.left, top: rect.top, width: rect.width, height: rect.height };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.currentTarget.focus({ preventScroll: true });
        event.preventDefault();
    };

    const drag = (event: React.PointerEvent<HTMLDivElement>) => {
        const starting = dragRef.current;
        if (!starting || starting.pointerId !== event.pointerId) return;
        movePopup(withinViewport({ left: starting.left + event.clientX - starting.x,
            top: starting.top + event.clientY - starting.y }, starting.width, starting.height, drawerSpace()));
    };

    const stopDrag = (event: React.PointerEvent<HTMLDivElement>) => {
        if (dragRef.current?.pointerId !== event.pointerId) return;
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };

    const moveWithKeyboard = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) return;
        const directions: Record<string, PopupPosition> = {
            ArrowLeft: { left: -1, top: 0 }, ArrowRight: { left: 1, top: 0 },
            ArrowUp: { left: 0, top: -1 }, ArrowDown: { left: 0, top: 1 },
        };
        const direction = directions[event.key];
        if (!direction || !paperRef.current) return;
        const rect = paperRef.current.getBoundingClientRect();
        const current = positionRef.current ?? { left: rect.left, top: rect.top };
        const amount = event.shiftKey ? 48 : 12;
        movePopup(withinViewport({ left: current.left + direction.left * amount,
            top: current.top + direction.top * amount }, rect.width, rect.height, drawerSpace()));
        event.preventDefault();
        event.stopPropagation();
    };

    const titleBarProps = {
        role: 'group' as const,
        'aria-label': 'Lesson review title bar',
        tabIndex: 0,
        title: 'Drag this bar to move the review; use arrow keys to move by 12 pixels, or Shift and an arrow key to move by 48 pixels',
        onPointerDown: startDrag,
        onPointerMove: drag,
        onPointerUp: stopDrag,
        onPointerCancel: stopDrag,
        onLostPointerCapture: () => { dragRef.current = null; },
        onKeyDown: moveWithKeyboard,
    };
    const titleBarStyle = {
        cursor: 'grab', touchAction: 'none', userSelect: 'none', bgcolor: '#F7F9FD',
        borderBottom: `1px solid ${BORDER}`,
        '&:active': { cursor: 'grabbing' },
        '&:focus-visible': { outline: '2px solid #2563EB', outlineOffset: -2 },
    };

    React.useEffect(() => {
        const rememberedId = Number(readSession(lessonId, activeStorageName));
        const next = validRouteIssueId ?? (Number.isSafeInteger(rememberedId) && rememberedId !== 0 ? rememberedId : null);
        setActiveId(next);
        if (validRouteIssueId) writeSession(lessonId, activeStorageName, String(validRouteIssueId));
        setFilter(readFilter(lessonId, pageScope));
        setDecisionError(null);
        setNavigationError(null);
    }, [lessonId, validRouteIssueId, activeStorageName, pageScope]);
    React.useEffect(() => {
        if (activeId === validRouteIssueId && issues.some(issue => issue.id === activeId && !needsReview(issue))) rememberFilter('all');
    }, [activeId, validRouteIssueId, issues, rememberFilter]);
    React.useEffect(() => {
        if (drawerOpen) setCollapsed(true);
    }, [drawerOpen]);
    // Only a request made while it is open folds it: one made before (the review reopened since) does not.
    const lastCopilotRequest = React.useRef(copilotRequest);
    React.useEffect(() => {
        if (copilotRequest && copilotRequest !== lastCopilotRequest.current) setCollapsed(true);
        lastCopilotRequest.current = copilotRequest;
    }, [copilotRequest]);
    React.useEffect(() => {
        if (open && !initialIssueId) paperRef.current?.focus({ preventScroll: true });
    }, [open, initialIssueId]);

    const changeCollapsed = React.useCallback((next: boolean) => {
        setCollapsed(next);
        writeSession(lessonId, 'collapsed', next ? 'true' : null);
    }, [lessonId]);

    const sorted = React.useMemo(() => [...issues].sort(issueOrder), [issues]);
    const visible = React.useMemo(() => filterIssues(sorted, filter), [sorted, filter]);
    const activeIndex = visible.findIndex(issue => issue.id === activeId);
    const selectedIndex = activeIndex >= 0 ? activeIndex : 0;
    const current = visible[selectedIndex];

    const openIssue = React.useCallback((next: IVerbaScholaLessonReviewIssue) => {
        setDecisionError(null);
        rememberActiveId(next.id);
        const reason = onOpen(next);
        setNavigationError(reason || null);
        if (!reason && (smallScreen || drawerOpen || next.action_required === 'generation_job')) changeCollapsed(true);
        if (next.action_required !== 'generation_job') {
            window.requestAnimationFrame(() => {
                const paper = paperRef.current;
                const nextButton = paper?.querySelector<HTMLButtonElement>('button[aria-label="Next review item"]:not(:disabled)');
                (nextButton ?? paper)?.focus({ preventScroll: true });
            });
        }
    }, [rememberActiveId, onOpen, smallScreen, drawerOpen, changeCollapsed]);

    React.useEffect(() => {
        if (!open || !focusRequest) return;
        if (handledFocusRequest.current?.lessonId === lessonId && handledFocusRequest.current.key === focusRequest.key) return;
        const issue = issues.find(item => item.id === focusRequest.issueId);
        if (!issue) return;
        handledFocusRequest.current = { lessonId, key: focusRequest.key };
        // A saved filter can hide the requested finding. Select its category first.
        rememberFilter(needsReview(issue) ? issue.source : 'all');
        if (!smallScreen && !drawerOpen) changeCollapsed(false);
        openIssue(issue);
    }, [open, focusRequest, issues, lessonId, rememberFilter, smallScreen, drawerOpen, changeCollapsed, openIssue]);

    const move = (direction: -1 | 1) => {
        if (!visible.length) return;
        openIssue(visible[(selectedIndex + direction + visible.length) % visible.length]);
    };

    const decide = async (status: 'accepted' | 'resolved') => {
        if (!current || decisionPending || !needsReview(current) || current.severity === 'error'
            || current.source === 'image' || current.action_required || current.category === 'picture_confirmation') return;
        setDecisionPending(true);
        setDecisionError(null);
        setNavigationError(null);
        try {
            await VerbaScholaApi.updateLessonReviewIssue(lessonId, current.id, status);
            // Retain the reader's place after the accepted finding leaves the open filter.
            rememberActiveId(visible.length > 1 ? visible[(selectedIndex + 1) % visible.length].id : null);
            window.dispatchEvent(new CustomEvent(LESSON_REVIEW_ISSUES_CHANGED, { detail: { lessonId } }));
            onChanged?.();
        } catch (requestError) {
            setDecisionError(lessonAuthoringError(requestError, 'Could not save the review decision. Refresh and try again.'));
            refresh();
        } finally {
            setDecisionPending(false);
        }
    };

    const filterOptions: { id: ReviewFilter; label: string }[] = [
        { id: 'open', label: `Needs review ${counts.open}` },
        ...(pageScope === 'plan' ? [] : [
            { id: 'image' as const, label: `Pictures ${issues.filter(issue => issue.source === 'image' && needsReview(issue)).length}` },
            { id: 'exercise' as const, label: `Exercises ${issues.filter(issue => issue.source === 'exercise' && needsReview(issue)).length}` },
        ]),
        ...(pageScope === 'exercises' ? [] : [
            { id: 'plan' as const, label: `Plan ${issues.filter(issue => issue.source === 'plan' && needsReview(issue)).length}` },
        ]),
        { id: 'all', label: `All ${issues.length}` },
    ];

    const contents = <Box role="region" aria-label="Lesson review" sx={{ p: 2, fontFamily: INTER }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}
            {...titleBarProps} sx={{ ...titleBarStyle, mx: -2, mt: -2, px: 2, py: 1.25 }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography component="h2" sx={{ fontWeight: 800, color: NAVY, fontSize: 18, lineHeight: 1.3 }}>Lesson review</Typography>
                {pageScope && <Typography variant="caption" sx={{ color: SLATE }}>{pageScope === 'plan' ? 'Plan page' : 'Exercises page'} · </Typography>}
                <Typography variant="caption" sx={{ color: SLATE }}>
                    {counts.error} errors · {counts.warning} warnings need attention
                </Typography>
            </Box>
            <Stack direction="row">
                <IconButton aria-label="Collapse lesson review" onClick={() => changeCollapsed(true)} size="small"><UnfoldLessIcon /></IconButton>
                <IconButton aria-label="Close lesson review" onClick={() => { changeCollapsed(false); onClose(); }} size="small"><CloseIcon /></IconButton>
            </Stack>
        </Stack>

        {navigationError && <Alert severity="warning" sx={{ mt: 1.25 }} action={current &&
            <Button color="inherit" size="small" onClick={() => openIssue(current)}>Retry</Button>}>
            {navigationError}
        </Alert>}

        <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, pb: 0.5, overflowX: 'auto' }} aria-label="Filter review items">
            {filterOptions.map(option => <Chip key={option.id} label={option.label} clickable size="small"
                color={filter === option.id ? 'primary' : 'default'} variant={filter === option.id ? 'filled' : 'outlined'}
                onClick={() => { rememberFilter(option.id); setDecisionError(null); }} />)}
        </Stack>

        {error && <Alert severity="error" sx={{ mt: 1.5 }} action={<Button color="inherit" size="small" onClick={refresh}>Retry</Button>}>{error}</Alert>}
        {loading && !issues.length ? <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} aria-label="Loading review items" /></Stack> : null}
        {!loading && !error && !current && <Alert severity="success" sx={{ mt: 1.5 }}>
            {filter === 'open' ? 'No items need review.' : 'No items match this filter.'}
        </Alert>}
        {current && <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" aria-live="polite" sx={{ fontWeight: 700, color: NAVY }}>
                    {selectedIndex + 1} of {visible.length}
                </Typography>
                <Stack direction="row" alignItems="center">
                    <Button size="small" onClick={() => openIssue(current)}>Show item</Button>
                    <IconButton size="small" aria-label="Previous review item" onClick={() => move(-1)} disabled={visible.length < 2}><ChevronLeftIcon /></IconButton>
                    <IconButton size="small" aria-label="Next review item" onClick={() => move(1)} disabled={visible.length < 2}><ChevronRightIcon /></IconButton>
                </Stack>
            </Stack>
            <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, borderColor: current.severity === 'error' ? '#F3A8AF' : '#E7D08B', boxShadow: 'none' }}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Chip size="small" label={current.severity === 'error' ? 'Error' : 'Review finding'}
                        sx={{ fontWeight: 700, color: current.severity === 'error' ? ERROR_TEXT : WARNING_TEXT,
                            bgcolor: current.severity === 'error' ? '#FFE5E5' : '#FFF2CB' }} />
                    <Chip size="small" variant="outlined" label={sourceLabel(current.source)} />
                    <Chip size="small" variant="outlined" label={categoryLabel(current.category)} />
                    {typeof current.period_number === 'number' && <Chip size="small" variant="outlined" label={`Period ${current.period_number}`} />}
                    {current.status === 'changed' && <Chip size="small" color="warning" label="Edited - review again" />}
                    {!needsReview(current) && <Chip size="small" label={categoryLabel(current.status)} />}
                </Stack>
                <LessonText sx={{ fontWeight: 750, color: NAVY, overflowWrap: 'anywhere' }}>{current.title}</LessonText>
                <LessonText variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{current.detail}</LessonText>
                {typeof current.score === 'number' && <Typography variant="body2" sx={{ mt: 0.75, color: WARNING_TEXT, fontWeight: 700 }}>Image review score: {Math.round(current.score)}/100</Typography>}
                {current.suggestion && <Box sx={{ mt: 1.25, p: 1, borderRadius: 1, bgcolor: '#F3F6FA' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: NAVY }}>Suggestion</Typography>
                    <LessonText variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{current.suggestion}</LessonText>
                </Box>}
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="contained" onClick={() => openIssue(current)}>{issueLocation(current)}</Button>
                    {needsReview(current) && current.severity === 'warning' && !current.action_required
                        && current.source !== 'image' && current.category !== 'picture_confirmation' && <>
                        <Button size="small" variant="outlined" disabled={decisionPending} onClick={() => void decide('accepted')}>{current.status === 'changed' ? 'Reviewed, accept as is' : 'Accept as is'}</Button>
                        <Button size="small" disabled={decisionPending} onClick={() => void decide('resolved')}>Mark fixed</Button>
                    </>}
                </Stack>
                {needsReview(current) && current.source === 'image' && current.severity === 'warning' &&
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: SLATE }}>Confirm this picture in the exercise picture checklist.</Typography>}
                {/* A failed request's notice already points to generation history, where its Retry is. */}
                {needsReview(current) && current.severity === 'error' && current.action_required !== 'generation_job' &&
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: SLATE }}>
                        {current.source === 'image'
                            ? 'Fix the picture in the editor. This error clears after the saved picture is valid.'
                            : 'Fix this item in the editor. This error clears after the saved content is valid.'}
                    </Typography>}
            </Paper>
            {decisionError && <Alert severity="error" sx={{ mt: 1 }}>{decisionError}</Alert>}
        </Box>}

        {visible.length > 1 && <Box sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => setShowList(value => !value)} aria-expanded={showList} aria-controls="lesson-review-item-list">
                {showList ? 'Hide item list' : `Browse all ${visible.length} items`}
            </Button>
            <Collapse in={showList}>
                <Stack id="lesson-review-item-list" role="list" spacing={0.5} sx={{ maxHeight: 170, overflowY: 'auto', pt: 0.5 }}>
                    {visible.map(issue => <Box role="listitem" key={issue.id}>
                        <Button fullWidth size="small" variant={issue.id === current?.id ? 'contained' : 'text'}
                            onClick={() => openIssue(issue)}
                            sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none' }}>
                            {issue.severity === 'error' ? 'Error' : 'Review'} · {sourceLabel(issue.source)} · {issue.title}
                        </Button>
                    </Box>)}
                </Stack>
            </Collapse>
        </Box>}
    </Box>;

    if (!open) return null;
    const compact = <Box role="region" aria-label="Lesson review" sx={{ fontFamily: INTER }}>
        <Stack direction="row" alignItems="center" spacing={0.25} {...titleBarProps}
            sx={{ ...titleBarStyle, px: 1, py: 0.5 }}>
            <IconButton aria-label="Expand lesson review" onClick={() => changeCollapsed(false)} size="small"><UnfoldMoreIcon /></IconButton>
            <Typography component="h2" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 800, color: NAVY, fontSize: 14 }}>
                Lesson review{pageScope ? ` · ${pageScope === 'plan' ? 'Plan' : 'Exercises'}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color: SLATE }}>
                {current ? `${selectedIndex + 1} of ${visible.length}` : `${counts.open} to review`}
            </Typography>
            <IconButton size="small" aria-label="Previous review item" onClick={() => move(-1)} disabled={visible.length < 2}><ChevronLeftIcon /></IconButton>
            <IconButton size="small" aria-label="Next review item" onClick={() => move(1)} disabled={visible.length < 2}><ChevronRightIcon /></IconButton>
            <IconButton aria-label="Close lesson review" onClick={() => { changeCollapsed(false); onClose(); }} size="small"><CloseIcon /></IconButton>
        </Stack>
        {current && <Typography variant="caption" noWrap sx={{ display: 'block', px: 1, py: 0.5, color: current.severity === 'error' ? ERROR_TEXT : WARNING_TEXT }}>{current.title}</Typography>}
        {navigationError && <Alert severity="warning" sx={{ mt: 0.5, '& .MuiAlert-message': { fontSize: 12 } }} action={current &&
            <Button color="inherit" size="small" onClick={() => openIssue(current)}>Retry</Button>}>
            {navigationError}
        </Alert>}
    </Box>;
    return <Paper ref={paperRef} elevation={8} role="dialog" aria-modal="false" aria-label="Lesson review navigator" tabIndex={-1}
        onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); onClose(); } }}
        // At first it sits at the top right, below the page's header and over the copilot, so the page beside it stays
        // readable and its controls stay put whatever the length of the item shown (a phone keeps it at the bottom).
        sx={{ position: 'fixed', right: position ? 'auto' : drawerOpen ? 'auto' : { xs: 12, sm: 20 },
            left: position?.left ?? (drawerOpen ? 12 : 'auto'),
            top: position?.top ?? (drawerOpen ? 72 : { xs: 'auto', sm: 88 }),
            bottom: position ? 'auto' : drawerOpen ? 'auto' : { xs: 12, sm: 'auto' },
            width: drawerOpen && collapsed ? 300 : { xs: 'calc(100vw - 24px)', sm: 420 }, maxWidth: 'calc(100vw - 24px)',
            maxHeight: { xs: 'min(55dvh, 480px)', sm: 'min(75vh, 700px)' },
            // Room for a scrollbar is kept whether or not the item needs one, so nothing shifts from item to item.
            overflowY: collapsed ? 'hidden' : 'auto', scrollbarGutter: collapsed ? undefined : 'stable', overscrollBehavior: 'contain', zIndex: theme.zIndex.modal - 1,
            border: `1px solid ${BORDER}`, borderRadius: 3 }}>{collapsed ? compact : contents}</Paper>;
}
